import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { ArrowRight, Calendar, FileText, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Facebook Page URL
const FACEBOOK_PAGE_URL = "https://www.facebook.com/SilviaCasaliCommercialista";
const FACEBOOK_PAGE_ID = "SilviaCasaliCommercialista";

const HomePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#D4AF37]/5 to-transparent" />
        <div className="max-w-6xl mx-auto px-6 py-20 lg:py-32 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl lg:text-6xl font-serif text-[#EDEDED] mb-4 leading-tight">
              Il Commercialista
            </h1>
            <p className="text-[#D4AF37] text-lg lg:text-xl uppercase tracking-[0.3em] mb-8">
              come partner strategico
            </p>
            <p className="text-[#A1A1AA] text-lg mb-10 max-w-xl">
              Gestisci i tuoi documenti fiscali in modo semplice e organizzato. 
              Carica fatture, estratti conto e molto altro in un'unica piattaforma sicura.
            </p>
            <div className="flex flex-wrap gap-4">
              {user ? (
                <Button
                  onClick={() => navigate("/dashboard")}
                  className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium rounded-sm transition-all duration-300 uppercase tracking-widest text-xs px-8 py-6"
                  data-testid="go-to-dashboard-btn"
                >
                  Vai alla Dashboard
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              ) : (
                <>
                  <Button
                    onClick={() => navigate("/login")}
                    className="bg-[#D4AF37] text-black hover:bg-[#B5952F] font-medium rounded-sm transition-all duration-300 uppercase tracking-widest text-xs px-8 py-6"
                    data-testid="login-btn"
                  >
                    Accedi
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Button
                    onClick={() => navigate("/register")}
                    variant="outline"
                    className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-medium rounded-sm transition-all duration-300 uppercase tracking-widest text-xs px-8 py-6"
                    data-testid="register-btn"
                  >
                    Registrati
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/30 to-transparent" />
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 border-b border-neutral-800">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-2xl lg:text-3xl font-serif text-[#EDEDED] text-center mb-12">
            Gestione Documentale Semplificata
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <FileText className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-medium text-[#EDEDED] mb-3">12 Categorie</h3>
              <p className="text-[#A1A1AA] text-sm">
                Fatture estere, carte di credito, paghe, estratti conto, commissioni POS, RIBA, assegni e molto altro
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
                <Calendar className="w-7 h-7 text-[#D4AF37]" />
              </div>
              <h3 className="text-lg font-medium text-[#EDEDED] mb-3">Organizzazione Mensile</h3>
              <p className="text-[#A1A1AA] text-sm">
                Documenti organizzati per anno e mese con indicatori visivi di completamento
              </p>
            </div>
            <div className="text-center p-6">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#10B981]/10 flex items-center justify-center">
                <div className="w-4 h-4 rounded-full bg-[#10B981]" />
              </div>
              <h3 className="text-lg font-medium text-[#EDEDED] mb-3">Stato Immediato</h3>
              <p className="text-[#A1A1AA] text-sm">
                Vedi subito cosa manca: grigio per documenti pendenti, verde per quelli caricati
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Facebook Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-center gap-3 mb-12">
            <Facebook className="w-6 h-6 text-[#D4AF37]" />
            <h2 className="text-2xl lg:text-3xl font-serif text-[#EDEDED]">
              Seguici su Facebook
            </h2>
          </div>
          
          <div className="flex justify-center">
            <div className="bg-[#0A0A0A] border border-neutral-800 rounded-lg overflow-hidden">
              {/* Facebook Page Plugin Container */}
              <div className="relative">
                <iframe
                  src="https://www.facebook.com/plugins/page.php?href=https%3A%2F%2Fwww.facebook.com%2FSilviaCasaliCommercialista&tabs=timeline&width=500&height=500&small_header=false&adapt_container_width=true&hide_cover=false&show_facepile=true&appId"
                  width="500"
                  height="500"
                  style={{ border: 'none', overflow: 'hidden', maxWidth: '100%', background: '#ffffff' }}
                  scrolling="no"
                  frameBorder="0"
                  allowFullScreen={true}
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  title="Facebook Page"
                  data-testid="facebook-plugin"
                />
              </div>
              
              {/* Link diretto alla pagina */}
              <div className="p-4 border-t border-neutral-800 text-center bg-[#0A0A0A]">
                <p className="text-[#A1A1AA] text-sm mb-3">
                  Strategie e conoscenza sempre aggiornati
                </p>
                <a
                  href={FACEBOOK_PAGE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#1877F2] text-white px-6 py-3 rounded-lg hover:bg-[#166FE5] transition-colors font-medium"
                  data-testid="facebook-link"
                >
                  <Facebook className="w-5 h-5" />
                  Apri la Pagina Facebook
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-800 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-[#52525B] text-sm">
            © {new Date().getFullYear()} Il Commercialista come partner strategico
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
